import React, { useState, useEffect } from 'react';
import { assets } from '../../assets/assets';

/**
 * Common Image component with reliable loading through ImageKit.io
 * This component handles all image loading in the frontend
 */
const ImageComponent = ({ 
  src, 
  alt, 
  className, 
  style, 
  fallback = null, 
  width = 400,
  height = 300,
  ...props 
}) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  
  // Get a food placeholder image from assets
  const getFoodPlaceholder = () => {
    // Use one of the food assets as fallback
    if (fallback) return fallback;
    return assets.food_1 || assets.food_2 || assets.food_3 || '/placeholder-food.png';
  };

  // Format image URL using ImageKit.io proxy for backend images
  const formatImageUrl = (url) => {
    if (!url) return null;
    
    // If it's already a data URL or blob URL, return as is
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return url;
    }
    
    // Backend URL
    const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL || 'https://dashbasket.onrender.com').replace(/\/$/, '');
    
    // If it's a path from the uploads directory or just a path without full URL
    if (url.startsWith('/uploads/') || (!url.startsWith('http') && !url.includes('/'))) {
      // Create full URL with backend
      const fullUrl = url.startsWith('/') 
        ? `${BACKEND_URL}${url}` 
        : `${BACKEND_URL}/${url}`;
      
      // Use ImageKit.io's proxy service for better CORS handling and caching
      return `https://ik.imagekit.io/demo/tr:w-${width},h-${height}/${encodeURIComponent(fullUrl)}`;
    }
    
    // If it's already an HTTP URL, use it directly
    if (url.startsWith('http')) {
      return url;
    }
    
    // Default case - return the URL as is
    return url;
  };
  
  useEffect(() => {
    setError(false);
    setLoading(true);
    
    if (!src) {
      setError(true);
      setLoading(false);
      return;
    }
    
    // Set the properly formatted URL
    const formattedUrl = formatImageUrl(src);
    setImageUrl(formattedUrl);
    
    // Preload the image using HTMLImageElement instead of Image constructor
    const imgElement = new window.Image();
    imgElement.crossOrigin = "anonymous"; // Try to avoid CORS issues
    imgElement.onload = () => setLoading(false);
    imgElement.onerror = () => {
      console.warn(`Image failed to load: ${src}`);
      setError(true);
      setLoading(false);
    };
    imgElement.src = formattedUrl;
  }, [src, width, height]);
  
  // Get default fallback image from assets if none provided
  const getFallbackImage = () => {
    if (fallback) return fallback;
    // Use one of the food assets as fallback
    return assets.food_1 || assets.food_2 || assets.food_3 || '/placeholder-food.png';
  };
  
  // Display loading state
  if (loading) {
    return (
      <div 
        className={className} 
        style={{ 
          ...style, 
          backgroundColor: '#f0f0f0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: height ? `${height/4}px` : '40px',
          minWidth: width ? `${width/4}px` : '40px'
        }}
      >
        <span style={{ fontSize: '10px', color: '#999' }}>Loading...</span>
      </div>
    );
  }
  
  // Display the image or fallback
  return (
    <img
      src={error ? getFallbackImage() : imageUrl}
      alt={alt || 'Image'}
      className={className}
      style={style}
      onError={() => setError(true)}
      {...props}
    />
  );
};

export default ImageComponent; 